import { useEffect, useMemo, useState } from "react";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, SegmentedControl, Skeleton } from "../components/ui";
import {
  ApiRequestError,
  fetchAiInsightResult,
  fetchAiInsightStatus,
  fetchCompletedAiInsights,
  updateAiInsightResult,
} from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useShellSession } from "../hooks/useShellSession";
import type { AIInsightCompletedJob, AIInsightRequest } from "../types";

type RangeDays = "1" | "3" | "7" | "30";
type ReviewStatusFilter = "ALL" | "COMPLETE" | "RUNNING" | "FAILED";
type SortOrder = "NEWEST" | "OLDEST";
type InsightEditMode = "GUIDED" | "JSON";

interface InsightView {
  overallAssessment: string;
  patterns: string[];
  gaps: string[];
  shoppingList: string[];
  quickWins: string[];
  assumptions: string[];
  dietitianNotes: string;
  generatedAt: string | null;
  version: string | null;
}

interface EditableDraft {
  overallAssessment: string;
  patternsText: string;
  gapsText: string;
  shoppingListText: string;
  quickWinsText: string;
  assumptionsText: string;
  dietitianNotes: string;
  resultJsonText: string;
}

type AnyObject = Record<string, unknown>;

function asObject(value: unknown): AnyObject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as AnyObject;
}

function toStringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    const single = toStringValue(value);
    return single ? [single] : [];
  }
  return value.map(toStringValue).filter((entry): entry is string => Boolean(entry));
}

function linesToItems(value: string): string[] {
  return value
    .split("\n")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function itemsToLines(items: string[]): string {
  return items.join("\n");
}

function formatDate(value?: string | null): string {
  if (!value) {
    return "Unknown";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function statusLabel(value?: string | null): string {
  const source = (value || "").trim();
  if (!source) {
    return "UNKNOWN";
  }
  return source.replace(/_/g, " ").toUpperCase();
}

function jobListKey(job: AIInsightCompletedJob): string {
  return (job.id || job.job_id || "").trim();
}

function jobRequestId(job?: AIInsightCompletedJob | null): string | null {
  const value = (job?.job_id || job?.id || "").trim();
  return value || null;
}

function extractResultRoot(payload: unknown): AnyObject | null {
  const root = asObject(payload);
  if (!root) {
    return null;
  }
  return asObject(root.result) ?? asObject(root.analysis) ?? asObject(root.insights) ?? root;
}

function extractInsightView(payload: unknown): InsightView {
  const result = extractResultRoot(payload);
  const pantrySummary = asObject(result?.pantry_summary);
  const nextSteps = asObject(result?.next_steps);
  const meta = asObject(result?.meta);

  return {
    overallAssessment:
      toStringValue(pantrySummary?.overall_assessment) ??
      toStringValue(pantrySummary?.summary) ??
      toStringValue(result?.overall_assessment) ??
      "No assessment available yet.",
    patterns: [
      ...toStringArray(pantrySummary?.patterns_noted),
      ...toStringArray(pantrySummary?.patterns),
      ...toStringArray(result?.patterns),
    ],
    gaps: [
      ...toStringArray(pantrySummary?.gaps_to_consider),
      ...toStringArray(pantrySummary?.gaps),
      ...toStringArray(result?.gaps),
    ],
    shoppingList: [
      ...toStringArray(nextSteps?.top_5_shopping_list),
      ...toStringArray(nextSteps?.shopping_list),
      ...toStringArray(result?.shopping_list),
    ],
    quickWins: [...toStringArray(nextSteps?.quick_wins), ...toStringArray(pantrySummary?.quick_wins)],
    assumptions: toStringArray(meta?.assumptions),
    dietitianNotes:
      toStringValue(result?.dietitian_notes) ??
      toStringValue(result?.clinical_notes) ??
      toStringValue(result?.reviewer_notes) ??
      "",
    generatedAt: toStringValue(meta?.generated_at),
    version: toStringValue(meta?.version),
  };
}

function buildDraftFromView(view: InsightView): EditableDraft {
  return {
    overallAssessment: view.overallAssessment,
    patternsText: itemsToLines(view.patterns),
    gapsText: itemsToLines(view.gaps),
    shoppingListText: itemsToLines(view.shoppingList),
    quickWinsText: itemsToLines(view.quickWins),
    assumptionsText: itemsToLines(view.assumptions),
    dietitianNotes: view.dietitianNotes,
    resultJsonText: "{}",
  };
}

function buildDraftFromPayload(payload: unknown): EditableDraft {
  const view = extractInsightView(payload);
  const draft = buildDraftFromView(view);
  const root = extractResultRoot(payload) ?? {};
  try {
    draft.resultJsonText = JSON.stringify(root, null, 2);
  } catch {
    draft.resultJsonText = "{}";
  }
  return {
    ...draft,
  };
}

function cloneObject<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function buildSavePayload(rawPayload: unknown, draft: EditableDraft): Record<string, unknown> {
  const payloadObject = asObject(rawPayload);
  const patterns = linesToItems(draft.patternsText);
  const gaps = linesToItems(draft.gapsText);
  const shoppingList = linesToItems(draft.shoppingListText);
  const quickWins = linesToItems(draft.quickWinsText);
  const assumptions = linesToItems(draft.assumptionsText);

  const applyEdits = (target: AnyObject): AnyObject => {
    const pantrySummary = asObject(target.pantry_summary) ?? {};
    pantrySummary.overall_assessment = draft.overallAssessment.trim() || null;
    pantrySummary.patterns_noted = patterns;
    pantrySummary.gaps_to_consider = gaps;
    pantrySummary.quick_wins = quickWins;

    const nextSteps = asObject(target.next_steps) ?? {};
    nextSteps.top_5_shopping_list = shoppingList;
    nextSteps.quick_wins = quickWins;

    const meta = asObject(target.meta) ?? {};
    meta.assumptions = assumptions;
    if (!toStringValue(meta.generated_at)) {
      meta.generated_at = new Date().toISOString();
    }
    meta.reviewed_at = new Date().toISOString();

    return {
      ...target,
      pantry_summary: pantrySummary,
      next_steps: nextSteps,
      dietitian_notes: draft.dietitianNotes.trim() || null,
      meta,
    };
  };

  if (!payloadObject) {
    return applyEdits({});
  }

  const cloned = cloneObject(payloadObject);
  if (asObject(cloned.result)) {
    return { ...cloned, result: applyEdits(asObject(cloned.result) ?? {}) };
  }
  if (asObject(cloned.analysis)) {
    return { ...cloned, analysis: applyEdits(asObject(cloned.analysis) ?? {}) };
  }
  if (asObject(cloned.insights)) {
    return { ...cloned, insights: applyEdits(asObject(cloned.insights) ?? {}) };
  }
  return applyEdits(cloned);
}

function buildJsonModePayload(draft: EditableDraft): Record<string, unknown> {
  let parsed: unknown = {};
  try {
    parsed = JSON.parse(draft.resultJsonText || "{}");
  } catch {
    throw new Error("Result JSON is invalid. Fix formatting before saving.");
  }
  const parsedObject = asObject(parsed);
  if (!parsedObject) {
    throw new Error("Result JSON must be an object.");
  }
  return { result: parsedObject };
}

function validateArrayOfStrings(value: unknown, label: string): string | null {
  if (!Array.isArray(value)) {
    return `${label} must be an array of text items.`;
  }
  for (const entry of value) {
    if (typeof entry !== "string") {
      return `${label} must contain only text items.`;
    }
  }
  return null;
}

function validateInsightResultPayload(payload: Record<string, unknown>): string | null {
  const root = asObject(payload);
  if (!root) {
    return "Payload must be an object.";
  }
  const resultRoot = asObject(root.result) ?? asObject(root.analysis) ?? asObject(root.insights) ?? root;
  if (!resultRoot) {
    return "Payload must include a valid result object.";
  }

  const pantrySummary = asObject(resultRoot.pantry_summary);
  if (resultRoot.pantry_summary !== undefined && !pantrySummary) {
    return "pantry_summary must be an object when provided.";
  }
  const nextSteps = asObject(resultRoot.next_steps);
  if (resultRoot.next_steps !== undefined && !nextSteps) {
    return "next_steps must be an object when provided.";
  }
  const meta = asObject(resultRoot.meta);
  if (resultRoot.meta !== undefined && !meta) {
    return "meta must be an object when provided.";
  }

  if (pantrySummary && !["string", "object"].includes(typeof pantrySummary.overall_assessment)) {
    return "pantry_summary.overall_assessment must be text or null.";
  }
  const patternsError = pantrySummary
    ? validateArrayOfStrings(
        pantrySummary.patterns_noted ?? pantrySummary.patterns ?? [],
        "pantry_summary.patterns_noted",
      )
    : null;
  if (patternsError) {
    return patternsError;
  }
  const gapsError = pantrySummary
    ? validateArrayOfStrings(
        pantrySummary.gaps_to_consider ?? pantrySummary.gaps ?? [],
        "pantry_summary.gaps_to_consider",
      )
    : null;
  if (gapsError) {
    return gapsError;
  }
  const quickWinsError = nextSteps ? validateArrayOfStrings(nextSteps.quick_wins ?? [], "next_steps.quick_wins") : null;
  if (quickWinsError) {
    return quickWinsError;
  }
  const shoppingError = nextSteps
    ? validateArrayOfStrings(nextSteps.top_5_shopping_list ?? nextSteps.shopping_list ?? [], "next_steps.top_5_shopping_list")
    : null;
  if (shoppingError) {
    return shoppingError;
  }
  const assumptionsError = meta ? validateArrayOfStrings(meta.assumptions ?? [], "meta.assumptions") : null;
  if (assumptionsError) {
    return assumptionsError;
  }
  const notesValue = resultRoot.dietitian_notes;
  if (!(notesValue === null || notesValue === undefined || typeof notesValue === "string")) {
    return "dietitian_notes must be text or null.";
  }
  return null;
}

export function DietitianInsightsReviewPage() {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);

  const [days, setDays] = useState<RangeDays>("7");
  const [statusFilter, setStatusFilter] = useState<ReviewStatusFilter>("ALL");
  const [sortOrder, setSortOrder] = useState<SortOrder>("NEWEST");
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<AIInsightCompletedJob[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedResolvedJobId, setSelectedResolvedJobId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<AIInsightRequest | null>(null);
  const [selectedRawResult, setSelectedRawResult] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editMode, setEditMode] = useState<InsightEditMode>("GUIDED");
  const [draft, setDraft] = useState<EditableDraft>({
    overallAssessment: "",
    patternsText: "",
    gapsText: "",
    shoppingListText: "",
    quickWinsText: "",
    assumptionsText: "",
    dietitianNotes: "",
    resultJsonText: "{}",
  });

  async function loadJobs(options?: { append?: boolean; cursor?: string | null }): Promise<void> {
    if (!isDietitian) {
      return;
    }
    const append = Boolean(options?.append);
    const cursor = options?.cursor ?? null;
    if (append) {
      setLoadingMoreJobs(true);
    } else {
      setLoadingJobs(true);
    }
    setJobsError(null);
    try {
      const payload = await fetchCompletedAiInsights(Number(days), cursor, 24);
      const items = payload.items ?? [];
      let merged: AIInsightCompletedJob[] = [];
      setJobs((current) => {
        if (!append) {
          merged = items;
          return merged;
        }
        const seen = new Set(current.map((entry) => jobListKey(entry)));
        const incoming = items.filter((job) => {
          const key = jobListKey(job);
          if (!key || seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        merged = [...current, ...incoming];
        return merged;
      });
      setNextCursor(payload.next_cursor ?? null);
      if (!selectedJobId && merged.length > 0) {
        setSelectedJobId(jobListKey(merged[0]) || null);
      } else if (selectedJobId && !merged.some((job) => jobListKey(job) === selectedJobId)) {
        setSelectedJobId(jobListKey(merged[0]) || null);
      }
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : "Unable to load completed jobs.");
    } finally {
      if (append) {
        setLoadingMoreJobs(false);
      } else {
        setLoadingJobs(false);
      }
    }
  }

  async function loadDetail(job: AIInsightCompletedJob | null, fallbackJobId?: string | null): Promise<void> {
    setLoadingDetail(true);
    setDetailError(null);
    setSelectedResolvedJobId(null);
    try {
      const candidates = [
        jobRequestId(job),
        (job?.id || "").trim() || null,
        (job?.job_id || "").trim() || null,
        (fallbackJobId || "").trim() || null,
      ].filter((entry): entry is string => Boolean(entry));
      const uniqueCandidates = Array.from(new Set(candidates));
      if (uniqueCandidates.length === 0) {
        throw new Error("Job not found");
      }

      let lastNotFoundError: Error | null = null;

      for (const candidateJobId of uniqueCandidates) {
        try {
          const resultPayload = await fetchAiInsightResult(candidateJobId);

          let statusPayload: AIInsightRequest | null = null;
          try {
            statusPayload = await fetchAiInsightStatus(candidateJobId);
          } catch (statusError) {
            if (!(statusError instanceof ApiRequestError && statusError.status === 404)) {
              throw statusError;
            }
          }

          setSelectedResolvedJobId(candidateJobId);
          setSelectedStatus(
            statusPayload ?? {
              id: candidateJobId,
              job_id: candidateJobId,
              status: (job?.status || "COMPLETE").toUpperCase(),
              created_at: job?.created_at,
              updated_at: job?.updated_at || job?.completed_at || undefined,
              summary: job?.summary ?? null,
              error_message: null,
            },
          );
          setSelectedRawResult(resultPayload);
          setDraft(buildDraftFromPayload(resultPayload));
          setEditMode("GUIDED");
          return;
        } catch (candidateError) {
          if (candidateError instanceof ApiRequestError && candidateError.status === 404) {
            lastNotFoundError = candidateError;
            continue;
          }
          throw candidateError;
        }
      }

      throw lastNotFoundError ?? new Error("Job not found");
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : "Unable to load insight details.");
      setSelectedStatus(null);
      setSelectedRawResult(null);
    } finally {
      setLoadingDetail(false);
      setEditing(false);
      setEditMode("GUIDED");
    }
  }

  useEffect(() => {
    void loadJobs({ append: false, cursor: null });
  }, [isDietitian, days]);

  useEffect(() => {
    if (!selectedJobId) {
      return;
    }
    const selected =
      jobs.find((entry) => jobListKey(entry) === selectedJobId || (entry.job_id || "").trim() === selectedJobId) ?? null;
    void loadDetail(selected, selectedJobId);
  }, [jobs, selectedJobId]);

  const visibleJobs = useMemo(() => {
    const normalizedStatusFilter = statusFilter.toUpperCase();
    const filtered =
      normalizedStatusFilter === "ALL"
        ? jobs
        : jobs.filter((job) => (job.status || "").toUpperCase() === normalizedStatusFilter);
    const sorted = [...filtered].sort((left, right) => {
      const leftTime = new Date(left.completed_at || left.updated_at || left.created_at || "").getTime();
      const rightTime = new Date(right.completed_at || right.updated_at || right.created_at || "").getTime();
      return sortOrder === "NEWEST" ? rightTime - leftTime : leftTime - rightTime;
    });
    return sorted;
  }, [jobs, sortOrder, statusFilter]);

  const selectedJob = useMemo(() => {
    if (!selectedJobId) {
      return null;
    }
    return (
      visibleJobs.find((entry) => jobListKey(entry) === selectedJobId) ??
      jobs.find((entry) => jobListKey(entry) === selectedJobId) ??
      null
    );
  }, [jobs, selectedJobId, visibleJobs]);
  const view = useMemo(() => extractInsightView(selectedRawResult), [selectedRawResult]);

  useEffect(() => {
    if (!selectedJobId) {
      if (visibleJobs.length > 0) {
        setSelectedJobId(jobListKey(visibleJobs[0]) || null);
      }
      return;
    }
    if (!visibleJobs.some((job) => jobListKey(job) === selectedJobId)) {
      setSelectedJobId((visibleJobs[0] && jobListKey(visibleJobs[0])) || null);
    }
  }, [selectedJobId, visibleJobs]);

  async function handleSave(): Promise<void> {
    if (saving) {
      return;
    }
    const requestJobId = selectedResolvedJobId || jobRequestId(selectedJob);
    if (!requestJobId) {
      setDetailError("Job not found");
      return;
    }
    setSaving(true);
    setDetailError(null);
    try {
      const payload = editMode === "JSON" ? buildJsonModePayload(draft) : buildSavePayload(selectedRawResult, draft);
      const validationError = validateInsightResultPayload(payload);
      if (validationError) {
        setDetailError(validationError);
        return;
      }
      const response = await updateAiInsightResult(requestJobId, payload);
      trackEvent("review_insight_saved", { job_id: requestJobId, edit_mode: editMode });
      setSelectedRawResult(response || payload);
      setDraft(buildDraftFromPayload(response || payload));
      setEditing(false);
      setEditMode("GUIDED");
      await loadJobs({ append: false, cursor: null });
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : "Unable to save insight changes.");
    } finally {
      setSaving(false);
    }
  }

  function clearSection(section: "patterns" | "gaps" | "shoppingList" | "quickWins" | "assumptions"): void {
    if (section === "patterns") {
      setDraft((current) => ({ ...current, patternsText: "" }));
      return;
    }
    if (section === "gaps") {
      setDraft((current) => ({ ...current, gapsText: "" }));
      return;
    }
    if (section === "shoppingList") {
      setDraft((current) => ({ ...current, shoppingListText: "" }));
      return;
    }
    if (section === "quickWins") {
      setDraft((current) => ({ ...current, quickWinsText: "" }));
      return;
    }
    setDraft((current) => ({ ...current, assumptionsText: "" }));
  }

  if (!isDietitian) {
    return (
      <div data-testid="dietitian-insights-review-page" className="space-y-4 pb-4">
        <PageHeader title="Dietitian AI review" subtitle="Dietitian access is required for cross-user AI insight review." />
        <ErrorState message="Your account does not have dietitian permissions for this workflow." />
      </div>
    );
  }

  return (
    <div data-testid="dietitian-insights-review-page" className="space-y-4 pb-4">
      <PageHeader title="Dietitian AI review" subtitle="Review completed insights for members and publish adjustments." />

      <Card>
        <div className="grid gap-3 md:grid-cols-[minmax(0,360px)_minmax(0,1fr)_auto]">
          <div>
            <p className="text-sm text-[var(--pr-text-muted)]">Time window</p>
            <SegmentedControl
              value={days}
              options={[
                { value: "1", label: "24h" },
                { value: "3", label: "3 days" },
                { value: "7", label: "7 days" },
                { value: "30", label: "30 days" },
              ]}
              onChange={setDays}
            />
          </div>
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">Status filter</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter((event.target.value as ReviewStatusFilter) || "ALL")}
              className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
            >
              <option value="ALL">All statuses</option>
              <option value="COMPLETE">Complete</option>
              <option value="RUNNING">Running</option>
              <option value="FAILED">Failed</option>
            </select>
          </label>
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">Sort by</span>
            <select
              value={sortOrder}
              onChange={(event) => setSortOrder((event.target.value as SortOrder) || "NEWEST")}
              className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
            >
              <option value="NEWEST">Newest first</option>
              <option value="OLDEST">Oldest first</option>
            </select>
          </label>
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <div className="space-y-3">
          {loadingJobs ? (
            <div className="space-y-2">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          ) : jobsError ? (
            <ErrorState message={jobsError} onRetry={() => void loadJobs({ append: false, cursor: null })} />
          ) : visibleJobs.length === 0 ? (
            <EmptyState title="No completed insights" subtitle="Completed insight jobs for this period will appear here." />
          ) : (
            visibleJobs.map((job) => {
              const listKey = jobListKey(job);
              const isSelected = selectedJobId === listKey;
              return (
                <button
                  key={listKey}
                  type="button"
                  onClick={() => { trackEvent("review_job_selected", { job_id: listKey }); setSelectedJobId(listKey); }}
                  className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                    isSelected
                      ? "border-[color-mix(in_srgb,var(--pr-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--pr-accent)_13%,transparent)]"
                      : "border-[var(--pr-border)] bg-[var(--pr-surface)]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">Insight review</p>
                    <Pill tone="accent" label={statusLabel(job.status)} />
                  </div>
                  <p className="mt-1 text-xs text-[var(--pr-text-muted)]">{job.user_name || "Member insight"}</p>
                  <p className="text-xs text-[var(--pr-text-muted)]">{formatDate(job.completed_at || job.updated_at || job.created_at)}</p>
                </button>
              );
            })
          )}
          {!loadingJobs && nextCursor ? (
            <Button
              variant="secondary"
              className="w-full"
              disabled={loadingMoreJobs}
              onClick={() => void loadJobs({ append: true, cursor: nextCursor })}
            >
              {loadingMoreJobs ? "Loading..." : "Load more jobs"}
            </Button>
          ) : null}
        </div>

        <div className="space-y-3">
          {!selectedJobId ? (
            <EmptyState title="Pick an insight job" subtitle="Select a completed AI insight run from the left to review details." />
          ) : loadingDetail ? (
            <div className="space-y-2">
              <Skeleton className="h-24" />
              <Skeleton className="h-36" />
              <Skeleton className="h-56" />
            </div>
          ) : detailError ? (
            <ErrorState message={detailError} onRetry={() => void loadDetail(selectedJob, selectedJobId)} />
          ) : (
            <>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Insight review details</h2>
                    <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
                      {selectedJob?.user_name || "Member insight"} · Updated{" "}
                      {formatDate(selectedStatus?.updated_at || selectedJob?.updated_at || selectedJob?.completed_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill tone="accent" label={statusLabel(selectedStatus?.status || selectedJob?.status)} />
                    <Button variant="secondary" className="min-h-10 px-3 text-sm" onClick={() => void loadDetail(selectedJob, selectedJobId)}>
                      Refresh
                    </Button>
                    <Button variant={editing ? "secondary" : "primary"} className="min-h-10 px-3 text-sm" onClick={() => { const next = !editing; trackEvent("review_edit_toggled", { editing: next }); setEditing(next); }}>
                      {editing ? "Preview" : "Edit recommendations"}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card data-testid="dietitian-ai-overview-card">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Overall assessment</p>
                <p className="mt-2 text-base leading-relaxed">{view.overallAssessment}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <Card flat>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Patterns</p>
                    <p className="pr-display mt-2 text-3xl font-semibold">{view.patterns.length}</p>
                  </Card>
                  <Card flat>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Priority gaps</p>
                    <p className="pr-display mt-2 text-3xl font-semibold">{view.gaps.length}</p>
                  </Card>
                  <Card flat>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Action steps</p>
                    <p className="pr-display mt-2 text-3xl font-semibold">{view.shoppingList.length + view.quickWins.length}</p>
                  </Card>
                </div>
              </Card>

              {editing ? (
                <Card>
                  <h3 className="text-base font-semibold">Edit structured recommendations</h3>
                  <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
                    Use Guided mode for common fields, or JSON mode to edit/add any result sections.
                  </p>
                  <div className="mt-3">
                    <SegmentedControl
                      value={editMode}
                      options={[
                        { value: "GUIDED", label: "Guided fields" },
                        { value: "JSON", label: "Full JSON" },
                      ]}
                      onChange={(value) => setEditMode((value as InsightEditMode) || "GUIDED")}
                    />
                  </div>
                  {editMode === "JSON" ? (
                    <div className="mt-3 grid gap-3">
                      <label className="flex flex-col gap-2 text-sm">
                        <span className="font-medium text-[var(--pr-text-muted)]">
                          Result JSON (any sections, keys, or arrays)
                        </span>
                        <textarea
                          rows={20}
                          value={draft.resultJsonText}
                          onChange={(event) => setDraft((current) => ({ ...current, resultJsonText: event.target.value }))}
                          className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 font-mono text-xs"
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-3">
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Overall assessment</span>
                      <textarea
                        rows={3}
                        value={draft.overallAssessment}
                        onChange={(event) => setDraft((current) => ({ ...current, overallAssessment: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Dietitian notes</span>
                      <textarea
                        rows={3}
                        value={draft.dietitianNotes}
                        onChange={(event) => setDraft((current) => ({ ...current, dietitianNotes: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="flex items-center justify-between gap-2 font-medium text-[var(--pr-text-muted)]">
                        <span>Patterns to maintain (one per line)</span>
                        <button
                          type="button"
                          onClick={() => clearSection("patterns")}
                          className="text-xs font-semibold text-[var(--pr-accent-deep)]"
                        >
                          Clear section
                        </button>
                      </span>
                      <textarea
                        rows={4}
                        value={draft.patternsText}
                        onChange={(event) => setDraft((current) => ({ ...current, patternsText: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="flex items-center justify-between gap-2 font-medium text-[var(--pr-text-muted)]">
                        <span>Gaps to improve (one per line)</span>
                        <button
                          type="button"
                          onClick={() => clearSection("gaps")}
                          className="text-xs font-semibold text-[var(--pr-accent-deep)]"
                        >
                          Clear section
                        </button>
                      </span>
                      <textarea
                        rows={4}
                        value={draft.gapsText}
                        onChange={(event) => setDraft((current) => ({ ...current, gapsText: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="flex items-center justify-between gap-2 font-medium text-[var(--pr-text-muted)]">
                        <span>Shopping list (one per line)</span>
                        <button
                          type="button"
                          onClick={() => clearSection("shoppingList")}
                          className="text-xs font-semibold text-[var(--pr-accent-deep)]"
                        >
                          Clear section
                        </button>
                      </span>
                      <textarea
                        rows={4}
                        value={draft.shoppingListText}
                        onChange={(event) => setDraft((current) => ({ ...current, shoppingListText: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="flex items-center justify-between gap-2 font-medium text-[var(--pr-text-muted)]">
                        <span>Quick wins (one per line)</span>
                        <button
                          type="button"
                          onClick={() => clearSection("quickWins")}
                          className="text-xs font-semibold text-[var(--pr-accent-deep)]"
                        >
                          Clear section
                        </button>
                      </span>
                      <textarea
                        rows={3}
                        value={draft.quickWinsText}
                        onChange={(event) => setDraft((current) => ({ ...current, quickWinsText: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="flex items-center justify-between gap-2 font-medium text-[var(--pr-text-muted)]">
                        <span>Assumptions (one per line)</span>
                        <button
                          type="button"
                          onClick={() => clearSection("assumptions")}
                          className="text-xs font-semibold text-[var(--pr-accent-deep)]"
                        >
                          Clear section
                        </button>
                      </span>
                      <textarea
                        rows={3}
                        value={draft.assumptionsText}
                        onChange={(event) => setDraft((current) => ({ ...current, assumptionsText: event.target.value }))}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button variant="secondary" className="min-h-10 px-3 text-sm" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                    <Button className="min-h-10 px-3 text-sm" onClick={() => void handleSave()} disabled={saving}>
                      {saving ? "Saving..." : "Save insight edits"}
                    </Button>
                  </div>
                </Card>
              ) : (
                <>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <Card>
                      <h3 className="text-base font-semibold">Patterns to maintain</h3>
                      {view.patterns.length === 0 ? (
                        <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No pattern highlights yet.</p>
                      ) : (
                        <ul className="mt-3 space-y-2 text-sm">
                          {view.patterns.map((pattern) => (
                            <li key={pattern} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                              {pattern}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Card>
                    <Card>
                      <h3 className="text-base font-semibold">Gaps to improve</h3>
                      {view.gaps.length === 0 ? (
                        <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No explicit gaps were returned.</p>
                      ) : (
                        <ul className="mt-3 space-y-2 text-sm">
                          {view.gaps.map((gap) => (
                            <li key={gap} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                              {gap}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Card>
                  </div>

                  <Card>
                    <h3 className="text-base font-semibold">Next steps</h3>
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      <Card flat>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Shopping list</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {view.shoppingList.length > 0 ? view.shoppingList.map((entry) => <Pill key={entry} label={entry} />) : <p className="text-sm text-[var(--pr-text-muted)]">No shopping list items.</p>}
                        </div>
                      </Card>
                      <Card flat>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Quick wins</p>
                        {view.quickWins.length > 0 ? (
                          <ul className="mt-2 space-y-2 text-sm">
                            {view.quickWins.map((entry) => (
                              <li key={entry} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                                {entry}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No quick wins included.</p>
                        )}
                      </Card>
                    </div>
                  </Card>

                  <Card>
                    <h3 className="text-base font-semibold">Dietitian notes</h3>
                    {view.dietitianNotes ? (
                      <p className="mt-2 text-sm leading-relaxed">{view.dietitianNotes}</p>
                    ) : (
                      <p className="mt-2 text-sm text-[var(--pr-text-muted)]">
                        No dietitian reviewer notes added for this job yet.
                      </p>
                    )}
                  </Card>

                  <Card>
                    <h3 className="text-base font-semibold">Analysis context</h3>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <Card flat>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Generated</p>
                        <p className="mt-1 text-sm font-semibold">{formatDate(view.generatedAt)}</p>
                      </Card>
                      <Card flat>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Model profile</p>
                        <p className="mt-1 text-sm font-semibold">{view.version || "Not provided"}</p>
                      </Card>
                    </div>
                    {view.assumptions.length > 0 ? (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Assumptions</p>
                        <ul className="mt-2 space-y-2 text-sm">
                          {view.assumptions.map((assumption) => (
                            <li key={assumption} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                              {assumption}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
